import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  DEFAULT_LOCALE,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  absoluteUrl,
  absoluteImage,
} from "@/config/seo";

interface SeoProps {
  title: string;
  description: string;
  /** Route path used to build the canonical URL. Falls back to legacy `path`. */
  canonicalPath?: string;
  /** @deprecated Prefer `canonicalPath`. Kept for backward compatibility. */
  path?: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "article";
  noindex?: boolean;
  /** Optional overrides — otherwise fall back to `title` / `description`. */
  ogTitle?: string;
  ogDescription?: string;
  /** @deprecated Prefer `image`. Kept for backward compatibility. */
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const Seo = ({
  title,
  description,
  canonicalPath,
  path,
  image,
  imageAlt,
  type = "website",
  noindex = false,
  ogTitle,
  ogDescription,
  ogImage,
  jsonLd,
}: SeoProps) => {
  const routePath = canonicalPath ?? path ?? "/";
  const url = absoluteUrl(routePath);
  const finalImage = absoluteImage(image ?? ogImage);
  const finalOgTitle = ogTitle ?? title;
  const finalOgDescription = ogDescription ?? description;
  const finalImageAlt = imageAlt ?? finalOgTitle;
  const robots = noindex
    ? "noindex,nofollow"
    : "index,follow,max-image-preview:large";

  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={DEFAULT_LOCALE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:secure_url" content={finalImage} />
      <meta property="og:image:alt" content={finalImageAlt} />
      <meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:image:alt" content={finalImageAlt} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
